import React from "react";
import mapboxgl from "!mapbox-gl"; // eslint-disable-line import/no-webpack-loader-syntax
import "mapbox-gl/dist/mapbox-gl.css";
import { Button } from "react-bootstrap";
import { FaMap } from "react-icons/fa";
import firebase from "utilities/firebase";
import { useSelector } from "react-redux";

mapboxgl.accessToken =
  "pk.eyJ1IjoiZW52aW9pZCIsImEiOiJja3g0bWx5NjgwN3MwMm9wYXB0cnpxdm43In0.F4GEDaOI0_o6fwWkL2r0lQ";

const MapBox = ({ destination, data }) => {
  const mapContainer = React.useRef(null);
  const map = React.useRef(null);
  const StateAuth = useSelector((state) => state?.Auth);

  const [pos, setPos] = React.useState(null);

  const start = [pos?.longitude, pos?.latitude];

  const geolocate = new mapboxgl.GeolocateControl({
    positionOptions: {
      enableHighAccuracy: true,
    },
    trackUserLocation: true,
    showUserHeading: true,
  });

  // create a function to make a directions request
  async function getRoute(end) {
    // make a directions request using driving profile
    // an arbitrary start will always be the same
    // only the end or destination will change
    const query = await fetch(
      `https://api.mapbox.com/directions/v5/mapbox/driving/${start[0]},${start[1]};${end[0]},${end[1]}?steps=true&geometries=geojson&access_token=${mapboxgl.accessToken}`,
      { method: "GET" }
    );
    const json = await query.json();
    const data = json?.code === "InvalidInput" ? null : json?.routes[0];
    const route = data && data.geometry.coordinates;
    const geojson = {
      type: "Feature",
      properties: {},
      geometry: {
        type: "LineString",
        coordinates: route,
      },
    };

    // if the route already exists on the map, we'll reset it using setData
    if (map.current.getSource("route")) {
      map.current.getSource("route").setData(geojson);
    }
    // otherwise, we'll make a new request
    else {
      map.current.addLayer({
        id: "route",
        type: "line",
        source: {
          type: "geojson",
          data: geojson,
        },
        layout: {
          "line-join": "round",
          "line-cap": "round",
        },
        paint: {
          "line-color": "#2f83ba",
          "line-width": 3,
          "line-opacity": 0.75,
        },
      });
    }
    // add turn instructions here at the end
  }

  React.useEffect(() => {
    if (map.current) return; // initialize map only once

    const bounds = [
      [94.36405868758938, -11.650356606220004], // Southwest coordinates
      [141.95026998017508, 6.763051702954545], // Northeast coordinates
    ];

    map.current = new mapboxgl.Map({
      container: mapContainer.current,
      style: "mapbox://styles/envioid/ckx5jzwyo23s714ob0srde3yk",
      center: [118.005351, -2.325613],
      zoom: 12,
      interactive: true,
      maxBounds: bounds,
    });
  });

  React.useEffect(() => {
    navigator.geolocation.getCurrentPosition(
      function (position) {
        setPos(position.coords);
      },
      (error) => {
        // See error code charts below.
        console.log(error.code, error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 60000,
        maximumAge: Infinity,
        distanceFilter: 1,
      }
    );
  }, []);

  React.useEffect(() => {
    if (!map.current) return; // wait for map to initialize
    map?.current?.addControl(geolocate);
    map.current.on("load", () => {
      geolocate.trigger();
    });
  }, []);

  React.useEffect(async () => {
    if (!map.current || !pos || !destination) return; // wait for map to initialize

    const finish = [destination?.longitude, destination?.latitude];

    map.current.on("load", () => {
      // make an initial directions request that
      // starts and ends at the same location
      getRoute(start);

      if (destination?.longitude !== 0 && destination?.latitude !== 0) {
        const end = {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: {},
              geometry: {
                type: "Point",
                coordinates: finish,
              },
            },
          ],
        };

        if (map.current.getLayer("end")) {
          map.current.getSource("end").setData(end);
        } else {
          map.current.addLayer({
            id: "end",
            type: "circle",
            source: {
              type: "geojson",
              data: {
                type: "FeatureCollection",
                features: [
                  {
                    type: "Feature",
                    properties: {},
                    geometry: {
                      type: "Point",
                      coordinates: finish,
                    },
                  },
                ],
              },
            },
            paint: {
              "circle-radius": 1,
            },
          });
        }

        new mapboxgl.Marker({ scale: 0.5, color: "#04E474" })
          .setLngLat(finish)
          .addTo(map?.current);
        getRoute(finish);
      }
    });

    if (destination?.longitude !== 0 && destination?.latitude !== 0) {
      map.current.fitBounds([start, finish], {
        maxZoom: 12,
        padding: 50,
      });
    } else {
      map.current.fitBounds([start, start], {
        maxZoom: 12,
        padding: 30,
      });
    }
    getRoute(finish);
  }, [pos, destination]);

  React.useEffect(() => {
    if (!data) return;

    let driverLoc = null;

    if (
      (data?.manifest_active &&
        data?.manifest_active?.trip_status === "started") ||
      (data && data?.trip_status === "started")
    ) {
      let ids = StateAuth?.session?.id;

      if (ids) {
        driverLoc = firebase.ref("driver/" + ids);
      }
    }

    let watch = navigator.geolocation.watchPosition(
      function (position) {
        setPos(position?.coords);

        if (driverLoc) {
          let payload = {
            latitude: position?.coords?.latitude,
            longitude: position?.coords?.longitude,
            timestamp: position?.timestamp,
          };
          driverLoc.set(payload);
        }
      },

      (error) => {
        // See error code charts below.
        console.log(error.code, error.message);
      },
      {
        enableHighAccuracy: true,
        timeout: 60000,
        maximumAge: Infinity,
        distanceFilter: 1,
      }
    );

    return () => {
      navigator.geolocation.clearWatch(watch);
    };
  }, []);

  React.useEffect(() => {
    if (!data) return;

    let destinationLoc = null;

    if (
      (data?.manifest_active &&
        data?.manifest_active?.trip_status === "started") ||
      (data && data?.trip_status === "started")
    ) {
      let ids = StateAuth?.session?.id;

      if (ids) {
        destinationLoc = firebase.ref("destination/" + ids);

        destinationLoc.set({
          latitude: destination?.latitude,
          longitude: destination?.longitude,
        });
      }
    }
  }, [data]);

  return (
    <div>
      <div className="position-relative">
        <div style={{ marginLeft: -20, marginRight: -20 }}>
          <div ref={mapContainer} className="map-container-small" />
        </div>

        <div className="position-absolute bottom-0 end-0 mb-1 me-2">
          <Button
            type="button"
            className="px-2 w-100 bg-primary-lt-2 border-primary text-secondary fs-7"
            onClick={() => {
              if (
                destination &&
                destination?.latitude === 0 &&
                destination?.longitude === 0
              ) {
                window.open(
                  `https://www.google.com/maps?saddr=My+Location&daddr=${destination?.region?.village_name?.replace(
                    " ",
                    "+"
                  )}+${destination?.region?.district_name?.replace(
                    " ",
                    "+"
                  )}+${destination?.region?.regency_name?.replace(
                    " ",
                    "+"
                  )}+${destination?.region?.province_name?.replace(" ", "+")}`,
                  "_blank"
                );
              } else {
                window.open(
                  `https://www.google.com/maps?saddr=My+Location&daddr=${destination?.latitude},${destination?.longitude}`,
                  "_blank"
                );
              }
            }}
          >
            <FaMap className="me-2" />
            Buka Google Maps
          </Button>
        </div>
      </div>
      {destination &&
      destination?.latitude === 0 &&
      destination?.longitude === 0 ? (
        <div className="bg-warning-lt text-center p-2 border-warning border rounded mt-2">
          <div className="text-secondary fs-6">
            <div>
              Lokasi tujuan tidak terdeteksi, silakan klik tombol Buka Google
              Maps untuk diarahkan dengan google maps.
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default MapBox;
